import { useState, type FormEvent } from "react";
import { Api, ApiError, type Me } from "../api.js";
import { errorText } from "../auth/Screens.js";
import { useI18n } from "../i18n.js";
import { TextInput } from "./ui.js";
import { UpgradeNote } from "./UpgradeNote.js";

/** What a brand-new account sees instead of an empty list: three steps and a short form that creates the first agent. */
export function Onboarding({ api, me, onOpen }: { api: Api; me: Me; onOpen: (id: string) => void }) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [job, setJob] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | string | null>(null);

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Only what the person typed goes in; the rest is filled in, and checked, in the steps that follow.
      const draft: Record<string, unknown> = { name: name.trim() };
      if (job.trim()) draft.persona = job.trim();
      onOpen((await api.createAgent(draft as never)).id);
    } catch (err) {
      setError(err instanceof ApiError ? err : errorText(err, t("common.network")));
      setBusy(false);
    }
  };

  const first = me.user.name.split(" ")[0] ?? "";
  return (
    <main className="page" id="main">
      <h1>{t("onboarding.title", { name: first })}</h1>
      <p>{t("onboarding.lead")}</p>
      <ol className="steps">
        <li>{t("onboarding.step1")}</li>
        <li>{t("onboarding.step2")}</li>
        <li>{t("onboarding.step3")}</li>
      </ol>
      {!me.user.emailVerified && <div className="banner warn">{t("onboarding.unverified")}</div>}
      <form className="card" onSubmit={start} noValidate>
        <TextInput label={t("onboarding.agentName")} value={name} onChange={setName} max={20} required help={t("onboarding.agentNameHelp")} />
        <TextInput label={t("onboarding.job")} value={job} onChange={setJob} multiline rows={3} help={t("onboarding.jobHelp")} />
        {error && (typeof error === "string" ? <div className="banner warn" role="alert">{error}</div> : error.status === 402 ? <UpgradeNote error={error} /> : <div className="banner warn" role="alert">{error.message}</div>)}
        <button className="btn primary" type="submit" disabled={busy || !name.trim() || name.trim().length > 20}>
          {t("onboarding.start")}
        </button>
      </form>
      <p className="hint aura-note">{t("onboarding.aura")}</p>
    </main>
  );
}
